import { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import Button from "app/storybookComponents/Button";
import {
  useCreateReflectionMutation,
  useDeleteReflectionMutation,
  useUpdateReflectionMutation,
} from "../goalsApi";
import "../Goal.scss";
import { GoalItem, GoalReflection, ReflectionStatus } from "../types";
import { Slider } from "app/shadcnComponents/ui/slider";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendar,
  faCircleCheck,
  faTrash,
  faUserPlus,
} from "@fortawesome/pro-regular-svg-icons";
import { cn } from "app/lib/utils";
import { useTextClamp } from "../hooks/useTextClamp";
import { getVisibilityToText } from "../helpers";
import { selectCurrentUserAccountId } from "app/containers/Global/slice";
import { useAppSelector } from "utils/redux/hooks";
import { AutoExpandTextarea } from "app/shadcnComponents/ui/AutoExpandTextarea";
import {
  trackGoalReflectionSaved,
  trackModalOpened,
} from "utils/trackingFunctions";

interface ReflectionModalProps {
  isOpen: boolean;
  hideModal: () => void;
  goal: GoalItem | null;
  reflection?: GoalReflection | null;
}

export default function ReflectionModal({
  isOpen,
  hideModal,
  goal,
  reflection,
}: Readonly<ReflectionModalProps>) {
  const currentUserAccountId = useAppSelector(selectCurrentUserAccountId);
  const isEditMode = !!reflection;
  const [deleteReflection, { isLoading: isDeleting }] =
    useDeleteReflectionMutation();

  const {
    textRef,
    isClampable,
    isExpanded: isGoalExpanded,
    setIsExpanded: setIsGoalExpanded,
  } = useTextClamp();

  const [createReflection, { isLoading: isCreating }] =
    useCreateReflectionMutation();
  const [updateReflection, { isLoading: isUpdating }] =
    useUpdateReflectionMutation();

  const [form, setForm] = useState<{
    progressPercent: number;
    status: ReflectionStatus;
    comments: string;
  }>({
    progressPercent: reflection?.progressPercent || 0,
    status: reflection?.status || "on_track",
    comments: reflection?.comments || "",
  });

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    trackModalOpened("Goal Reflection");
  }, [isOpen]);

  const maxChars = 250;

  const handleSlider = (value: number[]) => {
    setForm({ ...form, progressPercent: value[0] });
  };

  const selectStatus = (value: ReflectionStatus) => {
    setForm({ ...form, status: value });
  };

  const onSave = async () => {
    if (!goal) return;

    if (isEditMode && reflection) {
      await updateReflection({
        reflectionId: reflection.goalsReflectionsId,
        payload: {
          goalId: goal.goalId,
          progressPercent: form.progressPercent,
          status: form.status,
          comments: form.comments,
        },
      }).unwrap();
      trackGoalReflectionSaved({ isNewReflection: false });
    } else {
      await createReflection({
        goalId: goal.goalId,
        progressPercent: form.progressPercent,
        status: form.status,
        comments: form.comments,
      }).unwrap();
      trackGoalReflectionSaved({ isNewReflection: true });
    }
    hideModal();
  };

  const handleClose = () => {
    hideModal();
  };

  const getGoalSummarySection = () => {
    return (
      <div className="mb-[var(--spacing-lg)]">
        <div className="text-form-prompt font-medium text-primary-text mb-sm">
          Your goal
        </div>
        <div className="border-l-[3px] border-interactive bg-interactive-bg rounded-r-md p-[10px_16px]">
          <div
            className={cn(
              "text-[14px] font-medium text-primary-text mb-[4px] overflow-hidden [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2]",
              [isGoalExpanded && "d-block"]
            )}
            ref={textRef}
          >
            {goal?.title}
          </div>
          {isClampable && (
            <Button
              variant="link"
              onClick={() => setIsGoalExpanded(!isGoalExpanded)}
              className="my-[2px] text-[12px]"
            >
              Show {isGoalExpanded ? "less" : "more"}
            </Button>
          )}
          <div className="flex gap-2 items-center flex-wrap text-[12px] text-secondary">
            <FontAwesomeIcon
              icon={faCalendar}
              className="text-label"
              size="xs"
            />
            <span>{goal?.dueDateFormatted}</span>
            <span className="w-[3px] h-[3px] rounded-full bg-slate-40"></span>
            <FontAwesomeIcon
              icon={faCircleCheck}
              className="text-label"
              size="xs"
            />
            <span>{goal?.successCriteria}</span>
          </div>
        </div>
      </div>
    );
  };

  const getProgressSlider = () => {
    return (
      <div className="mb-lg">
        <div className="text-form-prompt font-medium text-primary-text mb-sm">
          How far along are you?
        </div>
        <div className="relative mb-2">
          <div className="my-3 d-flex gap-2">
            <Slider
              min={0}
              max={100}
              step={1}
              value={[form.progressPercent]}
              onValueChange={handleSlider}
              rangeClassName="bg-interactive"
              thumbClassName="border-interactive bg-interactive"
              trackClassName="bg-slate-10"
            />
            <div className="text-xl text-interactive font-medium fw-bold">
              {form.progressPercent}%
            </div>
          </div>
          <div className="d-flex justify-between mt-[-14px]">
            <div className="text-charcoal-60">
              <p>Just started</p>
            </div>
            <div className="text-charcoal-60">
              <p>Complete</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const getStatusSelector = () => {
    const unselectedButtonClassName =
      "bg-surface-1 border-input text-primary-text border-[1.5px] hover:bg-gray-50 hover:border-gray-400";
    const selectedButtonClassNames = {
      on_track:
        "bg-status-success-bg border-status-success text-status-success-text",
      some_challenges:
        "bg-status-warning-bg border-status-warning text-status-warning-text",
      unlikely_to_reach:
        "bg-status-error-bg border-status-error text-status-error-text",
    };
    return (
      <div className="mb-lg">
        <div className="text-[16px] font-medium text-primary-text mb-sm">
          How's it going?
        </div>
        <div className="flex gap-sm flex-wrap">
          <button
            type="button"
            onClick={() => selectStatus("on_track")}
            className={cn(
              "px-2 h-fit rounded font-medium transition-all text-sm  w-[140px] py-2 border-[1.5px]",
              form.status === "on_track"
                ? selectedButtonClassNames.on_track
                : unselectedButtonClassName
            )}
          >
            On Track
          </button>
          <button
            type="button"
            onClick={() => selectStatus("some_challenges")}
            className={cn(
              "px-2 h-fit rounded font-medium transition-all text-sm w-[140px] py-2 border-[1.5px]",
              form.status === "some_challenges"
                ? selectedButtonClassNames.some_challenges
                : unselectedButtonClassName
            )}
          >
            Some Challenges
          </button>
          <button
            type="button"
            onClick={() => selectStatus("unlikely_to_reach")}
            className={cn(
              "px-2 h-fit rounded font-medium transition-all text-sm w-[140px] py-2 border-[1.5px]",
              form.status === "unlikely_to_reach"
                ? selectedButtonClassNames.unlikely_to_reach
                : unselectedButtonClassName
            )}
          >
            Unlikely To Reach
          </button>
        </div>
      </div>
    );
  };

  const onDelete = async () => {
    if (!reflection?.goalsReflectionsId || !goal?.goalId) return;
    await deleteReflection({
      reflectionId: reflection?.goalsReflectionsId,
      goalId: goal?.goalId,
    }).unwrap();
    hideModal();
  };

  const isLoading = isEditMode ? isUpdating : isCreating;

  return (
    <Modal
      show={isOpen}
      onHide={handleClose}
      centered
      size="lg"
      backdrop="static"
      className="figtree-container"
    >
      <div className="d-flex align-items-center justify-content-between p-[16px_20px] border-b border-slate-10">
        <div className="text-[20px] font-bold leading-[28px] text-primary-text">
          {isEditMode ? "Edit Reflection" : "Reflection"}
        </div>
        <Button
          onClick={handleClose}
          variant="secondary-blue"
          xIcon
          style={{ border: "none" }}
        />
      </div>

      <div className="p-[var(--spacing-lg)]">
        {getGoalSummarySection()}
        {getProgressSlider()}
        {getStatusSelector()}
        <div>
          <div className="text-form-prompt font-medium text-primary-text mb-sm">
            Notes
          </div>
          <AutoExpandTextarea
            value={form.comments}
            onChange={(e) => {
              const comments = e.target.value;
              if (comments.length <= maxChars) {
                setForm((prevForm) => ({ ...prevForm, comments }));
              }
            }}
            placeholder="What's helping or getting in the way?"
            className="min-h-[72px] text-[14px] border-input bg-surface-1 rounded-md p-[10px_12px] focus:ring-0 focus:border-0 focus-visible:ring-0 focus-visible:border-0"
          />
          <div className="text-[11px] text-placeholder mt-1 text-right">
            {form.comments.length}/{maxChars}
          </div>
        </div>
      </div>

      <div className="flex justify-between items-center p-[12px_20px] border-t border-slate-10">
        <div className="flex items-center gap-2 text-[12px] text-secondary">
          <FontAwesomeIcon icon={faUserPlus} className="flex-shrink-0" />
          <span>
            {!!goal &&
              getVisibilityToText(
                goal,
                goal.userAccountId === currentUserAccountId
              )}
          </span>
        </div>
        <div className="flex gap-2">
          {isEditMode && goal ? (
            <Button
              variant="secondary-danger"
              onClick={onDelete}
              disabled={isDeleting}
            >
              <FontAwesomeIcon icon={faTrash} /> Delete
            </Button>
          ) : null}
          <Button className="btn-primary" onClick={onSave} disabled={isLoading}>
            {isLoading ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
